import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { telegramApi } from '../api/telegram';
import { toast } from 'react-hot-toast';
import { Send, MessageCircle, User, Clock, RefreshCw, ChevronLeft, CheckCircle, Trash2, MoreVertical, Image, FileText } from 'lucide-react';

const TelegramAdminPanel = () => {
    const { user, tokens } = useAuth();
    const { clearUnreadTelegramCount } = useWebSocket();
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [replyMessage, setReplyMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sendingReply, setSendingReply] = useState(false);
    const [botStatus, setBotStatus] = useState(null);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [imageUrls, setImageUrls] = useState({});
    const messagesEndRef = useRef(null);

    // Check if user is admin (roleId === 1)
    const isAdmin = user?.roleId === 1;

    useEffect(() => {
        if (isAdmin) {
            // Clear unread count when panel is opened
            clearUnreadTelegramCount();
            
            fetchBotStatus();
            fetchSessions();
            
            // Set up polling for new messages
            const interval = setInterval(() => {
                fetchSessions();
                if (selectedSession) {
                    fetchMessages(selectedSession.id);
                }
            }, 10000); // Poll every 10 seconds

            // Listen for real-time Telegram messages
            const handleTelegramMessage = (event) => {
                fetchSessions();
                if (selectedSession && event.detail.sessionId === selectedSession.id) {
                    fetchMessages(selectedSession.id);
                }
            };
            window.addEventListener('telegramMessageReceived', handleTelegramMessage);

            return () => {
                clearInterval(interval);
                window.removeEventListener('telegramMessageReceived', handleTelegramMessage);
            };
        }
    }, [isAdmin, selectedSession]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchBotStatus = async () => {
        try {
            const response = await telegramApi.getStatus(tokens.access.token);
            if (response.success) {
                setBotStatus(response.data);
            }
        } catch (error) {
            console.error('Error fetching bot status:', error);
        }
    };

    const fetchSessions = async () => {
        try {
            const response = await telegramApi.getSessions(tokens.access.token);
            if (response.success) {
                setSessions(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
        }
    };

    const fetchMessages = async (sessionId) => {
        setLoading(true);
        try {
            const response = await telegramApi.getSessionMessages(sessionId, tokens.access.token);
            if (response.success) {
                setMessages(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    };

    const handleSessionSelect = async (session) => {
        setSelectedSession(session);
        setShowActionMenu(false);
        await fetchMessages(session.id);
    };

    const handleResolveSession = async () => {
        if (!selectedSession) return;
        
        if (!window.confirm('Mark this conversation as resolved? The user will be notified.')) {
            return;
        }

        try {
            const response = await telegramApi.resolveSession(selectedSession.id, tokens.access.token);
            if (response.success) {
                toast.success('Conversation resolved!');
                setSelectedSession(null);
                setShowActionMenu(false);
                await fetchSessions();
            }
        } catch (error) {
            console.error('Error resolving session:', error);
            toast.error('Failed to resolve conversation');
        }
    };

    const handleDeleteSession = async () => {
        if (!selectedSession) return;
        
        if (!window.confirm('Delete this conversation? This cannot be undone.')) {
            return;
        }

        try {
            const response = await telegramApi.deleteSession(selectedSession.id, tokens.access.token);
            if (response.success) {
                toast.success('Conversation deleted!');
                setSelectedSession(null);
                setShowActionMenu(false);
                await fetchSessions();
            }
        } catch (error) {
            console.error('Error deleting session:', error);
            toast.error('Failed to delete conversation');
        }
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim() || !selectedSession) return;

        setSendingReply(true);
        try {
            const response = await telegramApi.sendReply(
                selectedSession.telegramChatId,
                replyMessage,
                tokens.access.token
            );
            
            if (response.success) {
                toast.success('Reply sent successfully!');
                setReplyMessage('');
                // Refresh messages
                await fetchMessages(selectedSession.id);
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            toast.error('Failed to send reply');
        } finally {
            setSendingReply(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeAgo = (dateString) => {
        if (!dateString) return '';
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    // Parse message content - check if it's a media message (JSON)
    const parseMessageContent = (content) => {
        // Handle null/undefined content
        if (!content) {
            return { type: 'text', content: '[No content]' };
        }
        
        try {
            const parsed = JSON.parse(content);
            if (parsed.type === 'photo' || parsed.type === 'document') {
                return parsed;
            }
        } catch {
            // Not JSON, return as plain text
        }
        return { type: 'text', content: content || '[No content]' };
    };

    // Load image URL from Telegram (proxied through backend) — fallback for old messages
    const loadImageUrl = async (fileId) => {
        if (!fileId || imageUrls[fileId]) return;
        setImageUrls(prev => (prev[fileId] ? prev : { ...prev, [fileId]: 'loading' }));
        try {
            const response = await telegramApi.getFileUrl(fileId, tokens.access.token);
            if (response.success && response.data?.url) {
                setImageUrls(prev => ({ ...prev, [fileId]: response.data.url }));
            } else {
                setImageUrls(prev => ({ ...prev, [fileId]: 'error' }));
            }
        } catch (error) {
            console.error('Error loading image:', error);
            setImageUrls(prev => ({ ...prev, [fileId]: 'error' }));
        }
    };

    // Render message content based on type
    const renderMessageContent = (msg) => {
        const parsed = parseMessageContent(msg.content);
        
        if (parsed.type === 'photo') {
            // Prefer Spaces URL (survives token changes); fall back to Telegram file_id
            const spacesUrl = parsed.url || null;
            if (!spacesUrl && parsed.fileId && !imageUrls[parsed.fileId]) {
                loadImageUrl(parsed.fileId);
            }
            
            const imageUrl = spacesUrl || (parsed.fileId ? imageUrls[parsed.fileId] : null);
            
            return (
                <div>
                    {imageUrl && imageUrl !== 'error' && imageUrl !== 'loading' ? (
                        <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                            <img 
                                src={imageUrl} 
                                alt="Photo" 
                                className="max-w-full rounded-lg mb-1 max-h-48 object-cover cursor-pointer hover:opacity-90"
                                onError={() => {
                                    if (!spacesUrl && parsed.fileId) {
                                        setImageUrls(prev => ({ ...prev, [parsed.fileId]: 'error' }));
                                    }
                                }}
                            />
                        </a>
                    ) : imageUrl === 'error' || (!spacesUrl && !parsed.fileId) ? (
                        <div className="flex items-center gap-2 text-sm opacity-70 text-red-400">
                            <Image className="w-4 h-4" />
                            <span>Failed to load image</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-sm opacity-70">
                            <Image className="w-4 h-4" />
                            <span>Loading image...</span>
                        </div>
                    )}
                    {parsed.caption && <p className="text-sm mt-1">{parsed.caption}</p>}
                </div>
            );
        }
        
        if (parsed.type === 'document') {
            const docContent = (
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    <div>
                        <p className="text-sm font-medium">{parsed.fileName || 'Document'}</p>
                        {parsed.caption && <p className="text-xs opacity-70">{parsed.caption}</p>}
                    </div>
                </div>
            );

            if (parsed.url) {
                return (
                    <a
                        href={parsed.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:opacity-80"
                    >
                        {docContent}
                    </a>
                );
            }

            return docContent;
        }

        // Plain text
        return <p className="whitespace-pre-wrap break-words text-sm">{msg.content}</p>;
    };

    if (!isAdmin) {
        return null; // Don't render for non-admin users
    }

    // Mobile: Full screen chat view when session is selected
    if (selectedSession) {
        return (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col h-[calc(100vh-180px)] min-h-[400px]">
                {/* Chat Header */}
                <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center gap-3">
                    <button
                        onClick={() => setSelectedSession(null)}
                        className="p-1 hover:bg-white/20 rounded text-white"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-medium text-white">{selectedSession.displayName || 'Unknown User'}</h3>
                        {selectedSession.telegramUsername && (
                            <p className="text-sm text-blue-100">@{selectedSession.telegramUsername}</p>
                        )}
                    </div>
                    <button
                        onClick={() => fetchMessages(selectedSession.id)}
                        className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                    <div className="relative">
                        <button
                            onClick={() => setShowActionMenu(!showActionMenu)}
                            className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
                            title="Actions"
                        >
                            <MoreVertical className="w-5 h-5" />
                        </button>
                        {showActionMenu && (
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
                                <button
                                    onClick={handleResolveSession}
                                    className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    Resolve
                                </button>
                                <button
                                    onClick={handleDeleteSession}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            <p>No messages in this conversation</p>
                        </div>
                    ) : (
                        [...messages].reverse().map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                                        msg.direction === 'outgoing'
                                            ? 'bg-blue-500 text-white rounded-br-md'
                                            : 'bg-white shadow-sm border border-gray-200 rounded-bl-md'
                                    }`}
                                >
                                    {renderMessageContent(msg)}
                                    <div className={`flex items-center gap-1 mt-1 text-xs ${
                                        msg.direction === 'outgoing' ? 'text-blue-100' : 'text-gray-400'
                                    }`}>
                                        <Clock className="w-3 h-3" />
                                        {formatDate(msg.createdAt)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                <form onSubmit={handleSendReply} className="p-3 border-t border-gray-200 bg-white safe-area-bottom">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder="Type your reply..."
                            className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            disabled={sendingReply}
                        />
                        <button
                            type="submit"
                            disabled={!replyMessage.trim() || sendingReply}
                            className="w-10 h-10 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                            {sendingReply ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    // Sessions list view
    return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {botStatus && (
                            <span className={`w-2 h-2 rounded-full ${
                                botStatus.initialized ? 'bg-green-400' : 'bg-red-400'
                            }`} />
                        )}
                        <span className="text-white text-sm font-medium">
                            {sessions.length} conversation{sessions.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <button
                        onClick={fetchSessions}
                        className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Sessions List */}
            <div className="max-h-[calc(100vh-280px)] min-h-[300px] overflow-y-auto">
                {sessions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 px-6">
                        <MessageCircle className="w-12 h-12 mb-3 opacity-50" />
                        <p className="text-center font-medium">No conversations yet</p>
                        <p className="text-sm text-center mt-1">Messages from Telegram users will appear here</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {sessions.map((session) => (
                            <div
                                key={session.id}
                                onClick={() => handleSessionSelect(session)}
                                className="p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <User className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-medium text-gray-900 truncate">
                                                {session.displayName || 'Unknown User'}
                                            </h4>
                                            <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                                {getTimeAgo(session.lastMessageAt)}
                                            </span>
                                        </div>
                                        {session.telegramUsername && (
                                            <p className="text-sm text-blue-600 truncate">
                                                @{session.telegramUsername}
                                            </p>
                                        )}
                                    </div>
                                    <ChevronLeft className="w-5 h-5 text-gray-400 rotate-180 flex-shrink-0" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TelegramAdminPanel;
